import { useQuery } from "@tanstack/react-query";

import { apiClient } from "@/services/api/client";

export function useOpsDashboardQuery(refresh = false) {
  return useQuery({
    queryKey: ["ops", "dashboard", refresh ? "refresh" : "cache"],
    queryFn: () => apiClient.getOpsDashboard({ refresh }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useOpsDatasyncBootstrapQuery() {
  return useQuery({
    queryKey: ["ops", "datasync", "bootstrap"],
    queryFn: () => apiClient.getOpsDatasyncBootstrap(),
    staleTime: 15_000,
    refetchInterval: 20_000,
  });
}

export function useOpsDatasyncRunsQuery(limit = 80) {
  return useQuery({
    queryKey: ["ops", "datasync", "runs", limit],
    queryFn: () => apiClient.listOpsDatasyncRuns({ limit }),
    refetchInterval: 5_000,
  });
}

export function useOpsDatasyncRunLogsQuery(runId: number | null, options?: { cursor?: number; limit?: number }) {
  const cursor = options?.cursor ?? 0;
  const limit = options?.limit ?? 200;
  return useQuery({
    queryKey: ["ops", "datasync", "run-logs", runId, cursor, limit],
    queryFn: () => apiClient.getOpsDatasyncRunLogs(runId as number, { cursor, limit }),
    enabled: runId != null,
    refetchInterval: 4_000,
  });
}

export function useOpsDatasyncRunStepsQuery(runId: number | null) {
  return useQuery({
    queryKey: ["ops", "datasync", "run-steps", runId],
    queryFn: () => apiClient.getOpsDatasyncRunSteps(runId as number),
    enabled: runId != null,
    refetchInterval: 6_000,
  });
}

export function useQuantPanelBootstrapQuery() {
  return useQuery({
    queryKey: ["quant", "panel", "bootstrap"],
    queryFn: () => apiClient.getQuantPanelBootstrap(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useQuantPanelRunsQuery(limit = 40) {
  return useQuery({
    queryKey: ["quant", "panel", "runs", limit],
    queryFn: () => apiClient.listQuantPanelRuns({ limit }),
    refetchInterval: 3_000,
  });
}

export function useQuantPanelRunQuery(runId: number | null) {
  return useQuery({
    queryKey: ["quant", "panel", "run", runId],
    queryFn: () => apiClient.getQuantPanelRun(runId as number),
    enabled: runId != null,
    refetchInterval: 3_000,
  });
}

export function useQuantPanelRunLogsQuery(runId: number | null, options?: { cursor?: number; limit?: number }) {
  const cursor = options?.cursor ?? 0;
  const limit = options?.limit ?? 200;
  return useQuery({
    queryKey: ["quant", "panel", "run-logs", runId, cursor, limit],
    queryFn: () => apiClient.getQuantPanelRunLogs(runId as number, { cursor, limit }),
    enabled: runId != null,
    refetchInterval: 3_000,
  });
}

export function useQuantPanelRunSelectsQuery(runId: number | null, limit = 200) {
  return useQuery({
    queryKey: ["quant", "panel", "run-selects", runId, limit],
    queryFn: () => apiClient.getQuantPanelRunSelects(runId as number, { limit }),
    enabled: runId != null,
    refetchInterval: 8_000,
  });
}

export function useQuantSelectsQuery(limit = 200, pickDate = "", enabled = true) {
  return useQuery({
    queryKey: ["quant", "selects", limit, pickDate],
    queryFn: () => apiClient.getQuantSelects({ limit, pickDate: pickDate || undefined }),
    enabled,
    refetchInterval: 15_000,
  });
}

export function useNewsFeedQuery(options: {
  category?: string;
  keyword?: string;
  tag?: string;
  limit?: number;
  offset?: number;
}) {
  const { category, keyword, tag, limit = 60, offset = 0 } = options;
  return useQuery({
    queryKey: ["news", "feed", category || "", keyword || "", tag || "", limit, offset],
    queryFn: () => apiClient.getNewsFeed({ category, keyword, tag, limit, offset }),
    staleTime: 30_000,
  });
}

export function useNewsArticleQuery(articlePath: string | null) {
  return useQuery({
    queryKey: ["news", "article", articlePath || ""],
    queryFn: () => apiClient.getNewsArticle(articlePath as string),
    enabled: Boolean(articlePath),
    staleTime: 30_000,
  });
}
